import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { capitalCase } from 'change-case';
import styled from 'styled-components';

import { CustomizedDialogs } from '../atoms';
import {
  clearRequestError,
  requestApiErrorObj,
} from '../redux/ducks/requestApiError';

type ErrorCopy = { headline: string; message: string };

const ERROR_MESSAGES: ErrorCopy[] = [
  {
    headline: 'Well, this is awkward.',
    message:
      "Something on our end just took an unscheduled coffee break. It's not you — it's us. Give it another try in a moment, and if it keeps happening our team would love to hear about it.",
  },
  {
    headline: 'Welp.',
    message:
      "Our server tripped over a cable and the request rolled away. We're picking it back up — give it another try in a moment.",
  },
  {
    headline: "That didn't go as planned.",
    message:
      'Something on our side hit the snooze button. It happens to the best of us. Try again, and if it keeps ghosting you, our team is on standby.',
  },
  {
    headline: 'Our bad, not yours.',
    message:
      "The server briefly forgot how to server. It's getting back into character now — please give that another shot.",
  },
  {
    headline: 'Hold that thought.',
    message:
      "We dropped your request behind the couch. We're fishing it out — try again in a few seconds and we should be back in business.",
  },
  {
    headline: 'Hiccup detected.',
    message:
      'Our backend just sneezed. Bless it. Please try once more — and if it sneezes again, let us know so we can find it some allergy meds.',
  },
  {
    headline: 'Plot twist.',
    message:
      "That request ran into something it didn't like and refused to elaborate. Give it another go — it's usually more chatty than this.",
  },
  {
    headline: 'Nothing to see here.',
    message:
      "Just a small detour on our end. We're rerouting around it — your next attempt should glide right through.",
  },
  {
    headline: 'Yeah, we saw that.',
    message:
      "Something on our side decided to take an early lunch. We're calling it back. One more try should do it — and if not, drop us a line.",
  },
  {
    headline: 'Awkward silence.',
    message:
      "The server didn't answer. We're nudging it now. Give that another try — and if it stays this quiet, we'd love to hear about it.",
  },
  {
    headline: 'Quick reroute.',
    message:
      "Looks like the bits took a wrong turn. We've sent them a map — please give it another go.",
  },
];

const ModalErrorRequest = () => {
  const dispatch = useDispatch();

  const requestApiError = useSelector(requestApiErrorObj);

  const copy = React.useMemo<ErrorCopy>(
    () =>
      ERROR_MESSAGES[
        Math.floor(Math.random() * ERROR_MESSAGES.length)
      ],
    [requestApiError.status, requestApiError.data],
  );

  const handleClose = () => {
    dispatch(clearRequestError());
  };

  // EA-1372: QBO-down hard-block on invoicing. When the backend returns the
  // machine-readable `qbo_unavailable` code, show ONLY the clean custom message
  // — no random headline, no status chip, and no raw body dump (Error:
  // qbo_unavailable / Non Field Errors ...).
  const errorData: any = requestApiError.data;
  const isQboUnavailable = errorData?.error === 'qbo_unavailable';
  // The error body is flattened by convertErrorResponseToValid, so `detail`
  // holds the clean human-readable message. Fall back to a fixed string.
  const qboMessage =
    (typeof errorData?.detail === 'string' && errorData.detail) ||
    'Invoicing is currently unavailable because the QuickBooks integration is down. Please try again later.';

  if (isQboUnavailable) {
    return (
      <StyledCustomizedDialogs onClose={handleClose}>
        <Wrapper>
          <IconBadge aria-hidden="true">
            <span role="img" aria-label="construction">
              🚧
            </span>
          </IconBadge>
          <Headline>Invoicing unavailable</Headline>
          <Message>{qboMessage}</Message>
          <Actions>
            <PrimaryButton type="button" onClick={handleClose}>
              OK
            </PrimaryButton>
          </Actions>
        </Wrapper>
      </StyledCustomizedDialogs>
    );
  }

  const details =
    requestApiError.data &&
    Object.entries(requestApiError.data).map(([key, value]) => (
      <DetailLine key={key}>
        {Number.isNaN(Number(key)) && key !== 'detail'
          ? `${capitalCase(key)}: ${value}`
          : String(value)}
      </DetailLine>
    ));

  return (
    <StyledCustomizedDialogs onClose={handleClose}>
      <Wrapper>
        <IconBadge aria-hidden="true">
          <span role="img" aria-label="construction">
            🚧
          </span>
        </IconBadge>
        <Headline>{copy.headline}</Headline>
        <Message>{copy.message}</Message>

        {(requestApiError.status || details) && (
          <DetailBlock>
            {requestApiError.status && (
              <StatusChip>
                Status {requestApiError.status}
              </StatusChip>
            )}
            {details}
          </DetailBlock>
        )}

        <Actions>
          <PrimaryButton type="button" onClick={handleClose}>
            Try again
          </PrimaryButton>
        </Actions>
      </Wrapper>
    </StyledCustomizedDialogs>
  );
};

const StyledCustomizedDialogs = styled(CustomizedDialogs)`
  && {
    position: fixed;
    z-index: 1500;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 8px 4px 0;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
`;

const IconBadge = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    #fff4d6 0%,
    #ffe2a7 100%
  );
  box-shadow: 0 8px 20px rgba(250, 176, 5, 0.18);

  span {
    font-size: 44px;
    line-height: 1;
  }
`;

const Headline = styled.h2`
  margin: 4px 0 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--mainText);
  letter-spacing: -0.01em;
`;

const Message = styled.p`
  margin: 0;
  max-width: 420px;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--neutral-600);
`;

const DetailBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 12px 16px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 8px;
  max-width: 460px;
  width: 100%;
  box-sizing: border-box;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--danger-50, #fef2f2);
  color: var(--danger-500, #dc2626);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const DetailLine = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-700);
  word-break: break-word;
`;

const Actions = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  min-width: 160px;
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

export default ModalErrorRequest;
